import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { JwtHelper } from '../helper/jwt-helper';
import { ServerPollService } from './server-poll.service';
import { Roles } from '@helper/enums/roles.enum';

interface ITokenContainer {
  token: string;
}

interface IUserInfoResultViewModel {
  version: string;
  isNew: boolean;
  isFreigegeben?: boolean;
  isExtern: boolean;
  isSub: boolean;
  userId: number;
  name: string;
  vorname: string;
  email: string;
  sub: string;
  role: any[];
}

@Injectable()
export class AuthService {
  private isLoggedIn = new Subject<boolean>();
  jwtHelper = new JwtHelper();

  constructor(
    private router: Router,
    private http: HttpClient,
    @Inject('BASE_URL') private baseUrl: string,
    private serverPollService: ServerPollService
  ) {}

  login(userRequest: any, cbOk: () => void = null, cbEr: (err: any) => void = null): void {
    const toSend = {
      userName: userRequest.username ? this.toBase64(userRequest.username) : '',
      password: userRequest.password ? this.toBase64(userRequest.password) : '',
    };
    this.http
      .post(this.baseUrl + 'api/Account/Token', toSend)
      .pipe(
        map((response) => {
          const token = (response as ITokenContainer).token;

          if (token) {
            this.userInfos(token).subscribe((userInfoToken) => {
              sessionStorage.setItem('user', JSON.stringify({ token: token, info: userInfoToken }));
              this.isLoggedIn.next(true);
              cbOk();
            });
          } else {
            this.isLoggedIn.next(false);
            cbEr(response);
          }
        })
      )
      .subscribe({
        next: null,
        error: (err) => {
          this.isLoggedIn.next(false);
          cbEr(err);
        },
      });
  }

  private toBase64(str: string): string {
    return btoa(new TextEncoder().encode(str).reduce((acc, b) => acc + String.fromCharCode(b), ''));
  }

  private userInfos(token: string): Observable<string> {
    return this.http
      .post(this.baseUrl + 'api/Account/UserInfo', null, {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + token,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
          'Content-Type': 'application/json',
        }),
      })
      .pipe(
        map((response) => {
          const token = (response as ITokenContainer).token;
          if (token) {
            return token;
          } else {
            return null;
          }
        })
      );
  }

  logout(): void {
    this.serverPollService.Stop();
    this.isLoggedIn.next(false);

    sessionStorage.removeItem('user');
    this.router.navigate(['/login']);
  }

  logoutWithError(error): void {
    this.serverPollService.Stop();
    this.isLoggedIn.next(false);

    sessionStorage.removeItem('user');
    this.router.navigate(['/login']);
    setTimeout(() => {
      throw error;
    }, 200);
  }

  public isAuth(): boolean {
    if (sessionStorage.getItem('user')) {
      this.isLoggedIn.next(true);
      return true;
    }

    this.isLoggedIn.next(false);
    return false;
  }

  getLoginState(): Observable<boolean> {
    return this.isLoggedIn.asObservable();
  }

  isSelf(id: number): boolean {
    const user = sessionStorage.getItem('user');
    const token = JSON.parse(user).info;
    const decodedIdToken = this.jwtHelper.decodeToken(token);
    if (+decodedIdToken.UserId === id) {
      return true;
    } else {
      return false;
    }
  }

  isTest(): boolean {
    const user = sessionStorage.getItem('user');
    const token = JSON.parse(user).info;
    const decodedIdToken = this.jwtHelper.decodeToken(token);
    return decodedIdToken.IsTest === 'True';
  }

  isNew(): boolean {
    const user = sessionStorage.getItem('user');
    const token = JSON.parse(user) ? JSON.parse(user).info : null;
    const decodedIdToken = token ? this.jwtHelper.decodeToken(token) : null;
    return decodedIdToken ? decodedIdToken.IsNew === 'True' : false;
  }

  isFreigegeben(): boolean {
    const user = sessionStorage.getItem('user');
    const token = JSON.parse(user) ? JSON.parse(user).info : null;
    const decodedIdToken = token ? this.jwtHelper.decodeToken(token) : null;
    return decodedIdToken ? decodedIdToken.isFreigegeben === 'True' : false;
  }

  isSelbstauskunftEnabled(): boolean {
    const user = sessionStorage.getItem('user');
    const token = user ? JSON.parse(user).info : null;
    const decodedIdToken = token ? this.jwtHelper.decodeToken(token) : null;
    return decodedIdToken?.SelbstauskunftEnabled === 'True';
  }

  isSelbstauskunftUnterschriftenblattEnabled(): boolean {
    const user = sessionStorage.getItem('user');
    const token = user ? JSON.parse(user).info : null;
    const decodedIdToken = token ? this.jwtHelper.decodeToken(token) : null;
    return decodedIdToken?.SelbstauskunftUnterschriftenblattEnabled === 'True';
  }

  isExtern(): boolean {
    const user = sessionStorage.getItem('user');
    const token = JSON.parse(user).info;
    const decodedIdToken = this.jwtHelper.decodeToken(token);
    return decodedIdToken.IsExtern === 'True';
  }

  isSub(): boolean {
    const user = sessionStorage.getItem('user');
    if (user) {
      const token = JSON.parse(user).info;
      const decodedIdToken = this.jwtHelper.decodeToken(token);
      return decodedIdToken.IsSub === 'True';
    }
    return false;
  }

  allowedActivities(): string[] {
    const user = sessionStorage.getItem('user');
    if (user) {
      const token = JSON.parse(user).info;
      const decodedIdToken = this.jwtHelper.decodeToken(token);
      const result = decodedIdToken ? (decodedIdToken.AllowedActivities && decodedIdToken.AllowedActivities.length > 0 ? JSON.parse(decodedIdToken.AllowedActivities) : []) : [];

      return result || [];
    }
    return [];
  }

  getUserName(): string {
    const user = sessionStorage.getItem('user');
    if (!user) {
      return '';
    }
    const token = JSON.parse(user).info;
    const decodedIdToken = this.jwtHelper.decodeToken(token);

    return decodedIdToken.sub;
  }

  getVersion(): string {
    const user = sessionStorage.getItem('user');
    if (!user) {
      return '';
    }
    const token = JSON.parse(user).info;
    const decodedIdToken = this.jwtHelper.decodeToken(token);

    return decodedIdToken.Version;
  }

  getUserEMail(): string {
    const user = sessionStorage.getItem('user');
    if (!user) {
      return '';
    }
    const token = JSON.parse(user).info;
    const decodedIdToken = this.jwtHelper.decodeToken(token);

    return decodedIdToken.Email;
  }

  getUserId(): number {
    const user = sessionStorage.getItem('user');
    if (!user) {
      return -1;
    }
    const token = JSON.parse(user).info;
    const decodedIdToken = this.jwtHelper.decodeToken(token);
    return decodedIdToken.UserId;
  }

  getNames(): any {
    const user = sessionStorage.getItem('user');

    if (!user) {
      return '';
    }

    const token = JSON.parse(user).info;
    const decodedIdToken = this.jwtHelper.decodeToken(token);
    return { name: decodedIdToken.Name, vorname: decodedIdToken.Vorname };
  }

  public darfInterneEoEBearbeiten(): boolean {
    return !this.isExtern() && this.hasOneRole(['Meldungen_Schreiben']) && this.hasOneRole(['Meldungen_Intern_Schreiben']);
  }

  public hasOneRole(rolesNeed: Roles[] | string[]): boolean {
    if (rolesNeed.length === 0) {
      return true;
    }

    let __rn: Roles[] = [];

    __rn = rolesNeed as Roles[];

    const user = sessionStorage.getItem('user');

    if (!user) {
      return false;
    }

    const token = JSON.parse(user).info;
    const decodedIdToken = this.jwtHelper.decodeToken(token);
    let _rolesHave = decodedIdToken.role as string[];

    if (!_rolesHave) {
      return false;
    }

    if (_rolesHave.indexOf(Roles.Administrator) !== -1) {
      return true;
    }

    const _rolesNeeded = __rn.map((x) => {
      return x.replace(/\s/g, '_').replace(/Ü/g, 'Ue');
    });

    if (!(_rolesHave instanceof Array)) {
      _rolesHave = [_rolesHave];
    }

    const _rn_tmp = _rolesNeeded.map((x) => this.splitIt(x));
    const _rh_tmp = _rolesHave.map((x) => this.splitIt(x));

    let result;

    for (let i = 0; i < _rn_tmp.length; i++) {
      const _tmp = _rh_tmp.filter((x) => x[0] === _rn_tmp[i][0]);

      let yes = 0;

      for (let u = 0; u < _tmp.length; u++) {
        const tmp = _tmp[u];
        if (tmp) {
          result = tmp;
          if (tmp[1] === 'Schreiben') {
            yes++;
          } else if (_rn_tmp[i][1] === 'Lesen' && tmp[1] === 'Lesen') {
            yes++;
          }
        }
      }

      if (yes > 0) {
        return true;
      }
    }

    return false;
  }

  private splitIt(str: string): string[] {
    const tmp = str.split('_');
    const last = tmp.pop();

    let result = [];

    if (tmp.length) {
      result = [tmp.join('_'), last];
    } else {
      result = [last];
    }

    return result;
  }
}
